'use client'

import { useEffect, useState } from 'react'

/**
 * Chiến lược whitelist: mặc định COI LÀ KHÔNG HỖ TRỢ mọi user agent, chỉ cho
 * qua khi nhận diện chắc chắn đó là một trình duyệt thật (Chrome, Safari,
 * Firefox, Edge, Cốc Cốc, Samsung Internet, Opera...). Ngược lại với cách
 * liệt kê từng app xấu (blacklist), whitelist bắt được cả app lạ/mới không
 * nằm trong danh sách — vì các in-app browser (Zalo, Messenger, TikTok...)
 * luôn chèn thêm chữ ký riêng hoặc token webview khiến UA không khớp bất kỳ
 * chữ ký trình duyệt thật nào bên dưới.
 *
 * Trên Android, WebView cổ điển luôn chèn token "; wv)" — cần loại trừ trước
 * khi so khớp "Chrome/", vì bản thân Android WebView cũng chứa "Chrome/".
 * Custom Tabs/TWA (mở "trong app" nhưng thực chất là Chrome thật) không có
 * token "; wv)" nên vẫn được coi là trình duyệt hợp lệ.
 *
 * Trên iOS không có heuristic tổng quát đáng tin cậy (nhiều in-app WebView
 * không tự thêm chữ ký riêng và giữ nguyên UA gần giống Safari thật), nên
 * whitelist chỉ nhận diện chắc chắn được Safari/Chrome/Firefox/Edge for iOS
 * qua token phiên bản riêng của từng hãng (CriOS, FxiOS, EdgiOS...).
 *
 * Riêng Zalo: dù mở link bằng Chrome Custom Tabs thật (UA khớp whitelist ở
 * trên), UA vẫn luôn chèn chữ "Zalo" — nên chặn thẳng theo token này trước,
 * bất kể whitelist có nhận diện được "trình duyệt thật" hay không.
 */
const ANDROID_GENERIC_WEBVIEW_PATTERN = /; ?wv\)/i

const ZALO_UA_PATTERN = /zalo/i

const KNOWN_REAL_BROWSER_UA_PATTERN =
  /CriOS\/|FxiOS\/|EdgiOS\/|OPiOS\/|Version\/[\d.]+.*Safari\/|Firefox\/|Edg\/|OPR\/|SamsungBrowser\/|CocCoc|UCBrowser\/|Chrome\//i

export function useInAppBrowser(): boolean {
  const [isInAppBrowser, setIsInAppBrowser] = useState(false)

  useEffect(() => {
    const ua = navigator.userAgent
    const isRecognizedRealBrowser =
      !ANDROID_GENERIC_WEBVIEW_PATTERN.test(ua) &&
      KNOWN_REAL_BROWSER_UA_PATTERN.test(ua)

    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing state from navigator.userAgent (external system)
    setIsInAppBrowser(ZALO_UA_PATTERN.test(ua) || !isRecognizedRealBrowser)
  }, [])

  return isInAppBrowser
}

/**
 * Mở trang hiện tại bằng trình duyệt hệ thống thay vì webview trong app.
 * Chỉ dùng cho Android/desktop — trên iOS không có cách nào để JS ép mở
 * Safari từ webview nhúng (Apple đã gỡ scheme `x-safari-https://` từ iOS 13),
 * nên UI trên iOS hiển thị ảnh hướng dẫn thao tác thủ công thay vì gọi hàm này.
 *
 * Android: dùng Intent URL không gắn cứng package trình duyệt, để Android tự
 * hiện dialog "Mở bằng..." cho người dùng chọn (Chrome, Cốc Cốc, ...).
 */
export function openInExternalBrowser() {
  if (typeof window === 'undefined') return

  const url = window.location.href

  if (/Android/i.test(navigator.userAgent)) {
    const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`
    window.location.href = intentUrl
    return
  }

  window.open(url, '_blank')
}
